import React, { useCallback, useState } from "react";
import { Row, Col } from "react-bootstrap";
import {
  Button, Flex, Modal, Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import xclIcon from "../../../assets/images/xlsIcon.png";
import dltIcon from "../../../assets/images/DeleteFunction.png";
import "./QcBulkUploadModal.css";
import { createMlQcRuleRange, createMlQcRuleSpecific } from "../../../actions/qc";
import QcBulkUploadFormatPreview from "./QcBulkUploadFormatPreview";
import QcBulkUploadResultModal from "./QcBulkUploadResultModal";

const { Dragger } = Upload;

const EXCEL_ACCEPT = "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_ACCEPT_EXTENSIONS = ".xls,.xlsx";
const SHEET_TO_ROWS_OPTS = { header: 1, defval: "" };
const RANGES_HEADER_ROW_COUNT = 2;
const RULES_HEADER_ROW_COUNT = 2;
const ResponseStatus = {
  PASSED: "passed",
  PARTIALLYUPLOADED: "partially-uploaded",
  FAILED: "failed",
};

const isExcelFile = (file) => /\.(xls|xlsx)$/i.test(file.name);

const getColumnIndexMap = (type = "ranges") => {
  if (type === "rules") {
    return {
      "Customer ID": 0,
      Scope: 1,
      "QC Pointers": 2,
      Specs: 3,
      "Sample Type": 4,
      "Match Scope": 5,
      "Test Category": 6,
      "Internal Name": 7,
      "Display Name": 8,
    };
  }
  return {
    "Customer ID": 0,
    Scope: 1,
    "Sample Info": 2,
    "Min Value": 3,
    "Max Value": 4,
    Unit: 5,
    "Match Scope": 6,
    "Test Category": 7,
    "Internal Name": 8,
    "Display Name": 9,
  };
};

const cellValue = (row, columnIndex) => {
  const value = row[columnIndex];
  if (value == null || value === "") return "";
  return String(value).trim();
};

const parseNumericCell = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildRangeGroupKey = (row, cols) => [
  cellValue(row, cols["Customer ID"]),
  cellValue(row, cols.Scope),
  cellValue(row, cols["Sample Info"]),
  cellValue(row, cols["Min Value"]),
  cellValue(row, cols["Max Value"]),
  cellValue(row, cols.Unit),
].join("\x1f");

const buildRulesGroupKey = (row, cols) => [
  cellValue(row, cols["Customer ID"]),
  cellValue(row, cols.Scope),
  cellValue(row, cols["Sample Type"]),
  cellValue(row, cols.Specs),
].join("\x1f");

const buildRegularTestFromRow = (row, cols) => {
  const matchScope = cellValue(row, cols["Match Scope"]);
  const testCategory = cellValue(row, cols["Test Category"]);
  const internalName = cellValue(row, cols["Internal Name"]);
  const displayName = cellValue(row, cols["Display Name"]);

  if (!testCategory && !internalName && !displayName) {
    return null;
  }

  return {
    match_scope: matchScope,
    test_category: testCategory,
    internal_name: internalName,
    display_name: displayName,
  };
};

/** @param {string[][]} data Excel sheet rows (includes header rows) */
export const buildRangesBulkUploadPayload = (data) => {
  if (!Array.isArray(data) || data.length <= RANGES_HEADER_ROW_COUNT) {
    return [];
  }

  const cols = getColumnIndexMap("ranges");
  const grouped = new Map();

  data.slice(RANGES_HEADER_ROW_COUNT).forEach((row) => {
    if (!Array.isArray(row) || row.every((cell) => String(cell ?? "").trim() === "")) {
      return;
    }

    const customerId = cellValue(row, cols["Customer ID"]);
    const groupKey = buildRangeGroupKey(row, cols);
    const regularTest = buildRegularTestFromRow(row, cols);

    if (!grouped.has(groupKey)) {
      const rangeType = "range";
      const minValue = parseNumericCell(cellValue(row, cols["Min Value"]));
      const maxValue = parseNumericCell(cellValue(row, cols["Max Value"]));
      const unit = cellValue(row, cols.Unit)?.trim() || "";
      const rawValue = (minValue !== null && maxValue !== null) ? `${minValue}${unit}-${maxValue}${unit}` : "";

      grouped.set(groupKey, {
        customer_id: customerId,
        scope: cellValue(row, cols.Scope).toLowerCase(),
        sample_info: cellValue(row, cols["Sample Info"]),
        regular_tests: [],
        range_spec: {
          type: rangeType,
          min_value: minValue,
          max_value: maxValue,
          unit,
          raw_value: rawValue,
        },
      });
    }

    if (regularTest) {
      grouped.get(groupKey).regular_tests.push(regularTest);
    }
  });

  return Array.from(grouped.values());
};

/** @param {string[][]} data Excel sheet rows (includes header rows) */
export const buildRulesBulkUploadPayload = (data) => {
  if (!Array.isArray(data) || data.length <= RULES_HEADER_ROW_COUNT) {
    return [];
  }

  const cols = getColumnIndexMap("rules");
  const grouped = new Map();

  data.slice(RULES_HEADER_ROW_COUNT).forEach((row) => {
    if (!Array.isArray(row) || row.every((cell) => String(cell ?? "").trim() === "")) {
      return;
    }

    const customerId = cellValue(row, cols["Customer ID"]);
    const groupKey = buildRulesGroupKey(row, cols);
    const regularTest = buildRegularTestFromRow(row, cols);

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        customer_id: customerId,
        scope: cellValue(row, cols.Scope).toLowerCase(),
        sample_info: {
          sample_type: cellValue(row, cols["Sample Type"]),
          regular_tests: [],
          qc_pointers: cellValue(row, cols["QC Pointers"]),
          specs: cellValue(row, cols.Specs),
        },
      });
    }

    if (regularTest) {
      grouped.get(groupKey).sample_info.regular_tests.push(regularTest);
    }
  });

  return Array.from(grouped.values());
};

const parseExcelFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  const rABS = !!reader.readAsBinaryString;

  reader.onload = (event) => {
    try {
      const bstr = event.target.result;
      const workbook = XLSX.read(bstr, {
        type: rABS ? "binary" : "array",
        bookVBA: true,
      });
      const rows = Object.fromEntries(
        workbook.SheetNames.map((name) => {
          const worksheet = workbook.Sheets[name];
          const matrix = XLSX.utils.sheet_to_json(worksheet, SHEET_TO_ROWS_OPTS);
          return [name, matrix];
        }),
      );
      resolve({
        file,
        sheetNames: workbook.SheetNames,
        rows,
      });
    } catch (err) {
      reject(err);
    }
  };

  reader.onerror = () => reject(new Error("Failed to read file"));

  if (rABS) {
    reader.readAsBinaryString(file);
  } else {
    reader.readAsArrayBuffer(file);
  }
});

export default function QcBulkUploadModal({
  open,
  type, // ranges,rules
  onCancel,
  title,
}) {
  const [xlsFiles, setXlsFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [showPreviewTable, setShowPreviewTable] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isPartialUpload, setIsPartialUpload] = useState(null);

  const resetAndClose = useCallback((isUploadSuccessfull = false) => {
    setXlsFiles([]);
    setParsing(false);
    setUploadResult(null);
    // if all items are successfully uploaded or only few items are uploaded successfully
    // refresh the data in main page
    onCancel({ isUploadSuccessfull: isUploadSuccessfull === true || isPartialUpload });
  }, [onCancel, isPartialUpload]);

  const deleteXls = useCallback((index) => {
    setXlsFiles((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, []);

  const onHandleXlsDrop = useCallback((acceptedFiles) => {
    const valid = acceptedFiles.filter(isExcelFile);
    const invalid = acceptedFiles.length - valid.length;
    if (invalid > 0) {
      toast.warn(`${invalid} file(s) skipped — only .xls and .xlsx are allowed`);
    }
    if (!valid.length) {
      return;
    }
    setXlsFiles(valid.slice(0, 1).map((file) => ({ file })));
  }, []);

  const beforeUpload = useCallback((file) => {
    onHandleXlsDrop([file]);
    return false;
  }, [onHandleXlsDrop]);

  const onSubmit = (dataType, rows) => {
    const isRangesType = dataType === "ranges";
    const buildPayload = isRangesType ? buildRangesBulkUploadPayload : buildRulesBulkUploadPayload;
    const submitPayload = isRangesType ? createMlQcRuleRange : createMlQcRuleSpecific;
    const payload = buildPayload(rows);
    if (payload.length === 0) {
      throw new Error("Empty excel file selected");
    }
    return submitPayload(payload);
  };

  const onSubmitParse = useCallback(async () => {
    if (!xlsFiles.length) {
      return;
    }
    setParsing(true);
    try {
      const results = await Promise.all(
        xlsFiles.map(({ file }) => parseExcelFile(file)),
      );
      if (results.length === 0) {
        throw new Error();
      }
      const data = results[0]; // taking only the first file
      const rows = data.rows[data.sheetNames[0]];
      const response = await onSubmit(type, rows);
      // if all failed
      if (!response.success && !response.canceled) {
        const failedResults = response.result ?? response.data?.result ?? [];
        if (failedResults.length > 0) {
          setUploadResult({ result: failedResults });
        }
        throw new Error(response.message);
      }
      // All items successfully uploaded
      const isAllSuccess = response.status === ResponseStatus.PASSED;
      if (isAllSuccess) {
        toast.success(response.message || "Uploaded successfully");
        resetAndClose(true); // close modal
      } else {
        // if partially passed
        setIsPartialUpload(true);
        toast.success(response.message);
        setUploadResult({
          result: response.result,
        });
      }
    } catch (err) {
      toast.error(err.message || "Could not parse Excel file");
    } finally {
      setParsing(false);
    }
  }, [type, xlsFiles, resetAndClose]);

  return (
    <Modal
      centered
      title={title}
      open={open}
      onCancel={() => resetAndClose()}
      footer={null}
      width={1080}
      destroyOnClose
      afterClose={() => {
        setXlsFiles([]);
        setUploadResult(null);
      }}
      rootClassName="QcBulkUploadModal-root"
    >
      <div className="QcBulkUploadModal">
        <Flex
          wrap
          className={`QcBulkUploadModal__body ${!showPreviewTable ? "QcBulkUploadModal__body--uploadOnly" : ""}`}
        >
          <Col xs={12} lg={showPreviewTable ? 5 : 12} className="QcBulkUploadModal__uploadCol">
            {!xlsFiles.length ? (
              <div className="QcBulkUploadModal__dropzone">
                <div className="QcBulkUploadModal__uploadHeader">Upload Files</div>
                <Dragger
                  className="QcBulkUploadModal__dragger"
                  name="file"
                  multiple={false}
                  accept={`${EXCEL_ACCEPT_EXTENSIONS},${EXCEL_ACCEPT}`}
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">Only .xls and .xlsx files are supported</p>
                </Dragger>
              </div>
            ) : (
              <>
                <div className="QcBulkUploadModal__uploadHeader"> Upload </div>
                <Row className="QcBulkUploadModal__previewSection">
                  {xlsFiles.map((image, imageIndex) => (
                    <Col key={image.file.name + String(imageIndex)}>
                      <div className="QcBulkUploadModal__previewCard">
                        <div>
                          <img
                            className="QcBulkUploadModal__previewThumb"
                            alt=""
                            height={70}
                            width={70}
                            src={xclIcon}
                          />
                          <span className="QcBulkUploadModal__fileName">{image.file.name}</span>
                        </div>
                        <div
                          className="QcBulkUploadModal__remove"
                          onClick={() => deleteXls(imageIndex)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              deleteXls(imageIndex);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <img src={dltIcon} width={14} alt="remove" />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </Col>
          {showPreviewTable && <QcBulkUploadFormatPreview type={type} />}
        </Flex>
        <div className="QcBulkUploadModal__actions">
          <Button
            type="default"
            className="QcBulkUploadModal__PreviewBtn"
            onClick={() => setShowPreviewTable(!showPreviewTable)}
          >
            Example
          </Button>
          <Button
            type="primary"
            onClick={onSubmitParse}
            loading={parsing}
            disabled={!xlsFiles.length}
          >
            Upload
          </Button>
        </div>
      </div>
      {!!uploadResult && (
        <QcBulkUploadResultModal
          open={!!uploadResult}
          type={type}
          result={uploadResult?.result}
          onCancel={() => setUploadResult(null)}
        />
      )}
    </Modal>
  );
}
